import React, { useState, useEffect, useContext } from 'react';
import { Layout, Menu, Flex, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { useSession } from 'next-auth/react';
import { useBranding } from '@src/theme/branding/useBranding';
import { TAssetAction } from '@src/types/assets';
import { useRouter } from 'next/router';
import Link from 'next/link';
import * as THREE from 'three';
import menuItems from './menu-items';
import AddToSceneModal from '../scene/AddToSceneModal';
import MyInventory from '../side-bar-menu/web2-asset-store/MyInventory';
import FreeAssets from '../side-bar-menu/web2-asset-store/FreeAssets';
import WorldsDrawer from '../side-bar-menu/worlds/WorldsDrawer';
import ZepetoInstructionsModal from './zepetoInstructionsModal';
import Web2AssetsDrawer from '../side-bar-menu/web2-asset-store/Web2AssetsDrawer';
import NewPaidCollection from '../side-bar-menu/web2-asset-store/NewPaidCollection';
import MyScenesDrawer from '../side-bar-menu/my-scenes';
import styles from './side-menu.module.css';
import { ASSETS_2022_CDN } from 'util/constants';
import { S3_BUCKET, CDN } from '@src/components/scene/assets/baseUrl';

export enum SIDE_MENU_EVENTS {
  OBJECTS_IN_SCENE = 0,
  SCENES = 1,
  LIBRARY_3D = 2,
  SKY = 3,
  LIGHTS = 4,
  NFT_ASSETS = 5,
  AUDIOS = 6,
  VIDEO_PLANE = 7,
  IMAGE_PLANE = 8,
  MY_STUFF = 9,
  SCREEN_SHARE = 10,
  CREATED_ASSETS = 11,
  LAND_ASSETS = 12,
  SHOPIFY = 13,
  QUESTS = 14,
  SVG_PLANE = 15,
  INITIAL_POINT = 16,
  AI_AVATARS = 17,
  TEXT_CHAT = 19,
  LIGHTSADDED = 20,
  HIDE_AND_SEEK = 21,
  SCAN_SCENE = 22,
  GRAPHICS_SETTINGS = 23,
  MFE_WORLDS = 24,
  POI = 25,
  PORTALS = 26,
  RPG_GAME = 27,
  RUNNING_GAME = 28,
  CHAT_GPT = 29,
  BIM_MODEL_SCANNER = 30,
  CUSTOM_CHARACTERS = 31,
  STABLE_DIFFUSION = 32,
  LEONARDO_AI = 33,
  VIDEO_AI = 34,
  OZONE_AI_MIND = 35,
  YOUTUBE = 36,
  TWITCH = 37,
  FACEBOOK = 38,
  ZEPETO_PACK = 39,
  TOUR = 40,
  IFRAME = 41,
  GAME_MODE = 42,
  SCENE_CONFIG = 43,
  THREE_D_GENERATION = 44,
  MML_OBJECTS = 45,
  TIMELINE_NODE_MANAGER = 46,
}

type Props = {
  callBack: Function;
  isAddedInitialPoint: boolean;
  openAssetStore: VoidFunction;
  addInitialPoint: VoidFunction;
  openMyStuff: VoidFunction;
  openGenerateImage: VoidFunction;
  isTransformControlsActive: boolean;
  setZepetoKey: Function;
  tourRefs: any;
  setIsTourOpen: Function;
  isAdmin: boolean;
  loadAsset: Function;
  MFESTemplatesScenes: Array<any>;
  sceneTemplates: any;
  setIsOpenWorlds: (value: boolean) => void;
  isOpenWorlds: boolean;
  selectedSceneTemplate: any;
  setSelectableAssets: (action: TAssetAction) => void;
  userEmail: string;
  buildScene?: any;
  camera?: any;
};

type MenuItem = Required<MenuProps>['items'][number];

const { Sider } = Layout;

const blankSceneTemplateURL = `${ASSETS_2022_CDN}/studioAssets/imageAssets/noSceneT.jpg`;

export default function SideMenu({
  callBack,
  isAddedInitialPoint,
  addInitialPoint,
  loadAsset,
  openAssetStore,
  openMyStuff,
  openGenerateImage,
  isTransformControlsActive,
  setZepetoKey,
  tourRefs,
  setIsTourOpen,
  isAdmin,
  MFESTemplatesScenes,
  sceneTemplates,
  setIsOpenWorlds,
  isOpenWorlds,
  selectedSceneTemplate,
  setSelectableAssets,
  userEmail,
  buildScene,
  camera,
}: Props) {
  const { data: session } = useSession();
  const { isZepeto, isXsolla } = useBranding();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [menuSelected, setMenuSelected] = useState<string[]>([]);
  const [isZepetoInstructionsVisible, setZepetoInstructionsVisible] =
    useState<boolean>(false);
  const [isOpenWeb2AssetStore, setIsOpenWeb2AssetStore] =
    useState<boolean>(false);
  const [isOpenMyInventory, setIsOpenMyInventory] = useState<boolean>(false);
  const [isOpenFreeAssets, setIsOpenFreeAssets] = useState<boolean>(false);
  const [isOpenMyScenes, setIsOpenMyScenes] = useState<boolean>(false);
  const [isOpenNewPaidCollection, setIsOpenNewPaidCollection] =
    useState<boolean>(false);
  const [isAddToSceneModalOpen, setIsAddToSceneModalOpen] =
    useState<boolean>(false);

  const worldsWithFallback = React.useMemo(() => {
    let sceneTemplatesFromDB: any[] = [];

    if (
      sceneTemplates?.sceneTemplates &&
      sceneTemplates.sceneTemplates.length > 0
    ) {
      sceneTemplatesFromDB = sceneTemplates.sceneTemplates.map((t) => ({
        ...t,
        baseModelURL: t.baseModelURL?.replace(S3_BUCKET, CDN),
        thumbnail: t.thumbnail?.replace(S3_BUCKET, CDN),
      }));
    } else {
      sceneTemplatesFromDB = [
        {
          id: 'fallback-0',
          name: 'Art Gallery',
          baseModelURL: `${ASSETS_2022_CDN}/studioAssets/artGallery/`,
          thumbnail: `${ASSETS_2022_CDN}/studioAssets/imageAssets/artGallery.jpg`,
        },
        {
          id: 'fallback-1',
          name: 'Board Room',
          baseModelURL: `${ASSETS_2022_CDN}/studioAssets/boardRoom/`,
          thumbnail: `${ASSETS_2022_CDN}/studioAssets/imageAssets/boardRoom.jpg`,
        },
        {
          id: 'fallback-2',
          name: 'Class Room',
          baseModelURL: `${ASSETS_2022_CDN}/studioAssets/classRoom/`,
          thumbnail: `${ASSETS_2022_CDN}/studioAssets/imageAssets/classRoom.jpg`,
        },
        {
          id: 'fallback-3',
          name: 'Open Auditorium',
          baseModelURL: `${ASSETS_2022_CDN}/studioAssets/openAuditorium/`,
          thumbnail: `${ASSETS_2022_CDN}/studioAssets/imageAssets/openAuditorium.jpg`,
        },
        {
          id: 'fallback-4',
          name: 'Conference Room',
          baseModelURL: `${ASSETS_2022_CDN}/studioAssets/conferenceRoom/`,
          thumbnail: `${ASSETS_2022_CDN}/studioAssets/imageAssets/conferenceRoom.jpg`,
        },
      ];
    }

    const sceneTemplateWithBlank = [
      {
        id: '',
        name: 'No Scene Template',
        baseModelURL: '',
        thumbnail: blankSceneTemplateURL,
      },
      ...sceneTemplatesFromDB,
    ];

    return sceneTemplateWithBlank;
  }, [sceneTemplates]);

  const handleCallback = (type: SIDE_MENU_EVENTS) => {
    if (type === SIDE_MENU_EVENTS.SCENES) {
      setIsOpenWorlds(true);
    } else {
      callBack(type);
    }
  };

  const items: MenuItem[] = React.useMemo(
    () => [
      ...menuItems({
        branding: isZepeto ? 'ZEPETO' : isXsolla ? 'XSOLLA' : 'DEFAULT',
        callBack: handleCallback,
        setMenuSelected,
        tourRefs,
        styles,
        openAssetStore,
        openMyStuff,
        isAddedInitialPoint,
        addInitialPoint,
        setZepetoKey,
        setZepetoInstructionsVisible,
        setIsTourOpen,
        isAdmin,
        setIsOpenWeb2AssetStore,
        setIsOpenMyInventory,
        setIsOpenFreeAssets,
        setIsOpenMyScenes,
        setIsOpenWorlds,
        isSignIn: session?.user.id ? true : false,
        setIsOpenNewPaidCollection,
        onOpenAddToSceneModal: () => setIsAddToSceneModalOpen(true),
      }),
    ],
    [MFESTemplatesScenes, isZepeto, isXsolla]
  );

  return (
    <div className={styles.root}>
      <Sider
        id='side-menu'
        className={styles.sider}
        collapsible
        theme='light'
        collapsed={collapsed}
        width={250}
        onCollapse={(value) => setCollapsed(value)}
        trigger={
          <Tooltip
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            placement='right'
          >
            <div className={styles.collapseButton}>{collapsed ? '→' : '←'}</div>
          </Tooltip>
        }
      >
        <Menu
          defaultSelectedKeys={['1']}
          mode='inline'
          items={items}
          className={'scene-editor-menu'}
          selectedKeys={menuSelected}
        />
        {isZepeto && (
          <Flex
            className={styles.policyLinks}
            vertical
            gap={10}
            justify='center'
            align='center'
          >
            <Link href={`/privacy-policy`}>Privacy Policy</Link>
            <Link href={`/terms-of-use`}>Terms of Use</Link>
            <a
              href={`${ASSETS_2022_CDN}/Global/zepetoAssets/AssetStoreCredentials.pdf`}
              target='_blank'
            >
              Сreators Credits
            </a>
          </Flex>
        )}
        {isXsolla && (
          <Flex
            className={styles.policyLinks}
            vertical
            gap={10}
            justify='center'
            align='center'
          >
            <Link href={`/privacy-policy`}>Privacy Policy</Link>
            <Link href={`/terms-of-use`}>Terms of Use</Link>
          </Flex>
        )}
        <ZepetoInstructionsModal
          isOpen={isZepetoInstructionsVisible}
          setIsOpen={setZepetoInstructionsVisible}
        />
        <Web2AssetsDrawer
          isOpen={isOpenWeb2AssetStore}
          setIsOpen={setIsOpenWeb2AssetStore}
          userEmail={session?.user.email}
        />
        <NewPaidCollection
          isOpen={isOpenNewPaidCollection}
          setIsOpen={setIsOpenNewPaidCollection}
          userEmail={session?.user.email}
        />
        <MyScenesDrawer isOpen={isOpenMyScenes} setIsOpen={setIsOpenMyScenes} />
        <WorldsDrawer
          isOpen={isOpenWorlds}
          setIsOpen={setIsOpenWorlds}
          loadScene={(name, url, type, sceneThumbnail) => {
            loadAsset({
              name,
              type,
              baseModelURL: url,
              thumbnail: url,
              sceneThumbnail,
            });
          }}
          worlds={worldsWithFallback}
          selectedWorld={selectedSceneTemplate}
        />
        <MyInventory
          isOpen={isOpenMyInventory}
          setIsOpen={setIsOpenMyInventory}
          loadAsset={(name: string, url: string, type: SIDE_MENU_EVENTS) => {
            loadAsset({ name, type, url, thumbnail: url });
          }}
          loadScene={(
            name: string,
            url: string,
            type: SIDE_MENU_EVENTS,
            sceneThumbnail: string
          ) => {
            loadAsset({
              name,
              type,
              baseModelURL: url,
              thumbnail: url,
              sceneThumbnail,
            });
          }}
        />
        <FreeAssets
          isOpen={isOpenFreeAssets}
          setIsOpen={setIsOpenFreeAssets}
          loadAsset={(name: string, url: string, type: SIDE_MENU_EVENTS) => {
            loadAsset({ name, type, url, thumbnail: url });
          }}
          loadScene={(
            name: string,
            url: string,
            type: SIDE_MENU_EVENTS,
            sceneThumbnail: string
          ) => {
            loadAsset({
              name,
              type,
              baseModelURL: url,
              thumbnail: url,
              sceneThumbnail,
            });
          }}
        />
        <AddToSceneModal
          open={isAddToSceneModalOpen}
          onClose={() => setIsAddToSceneModalOpen(false)}
          setSelectableAssets={setSelectableAssets}
          userEmail={userEmail}
          buildScene={buildScene}
          session={session}
          worlds={worldsWithFallback}
          selectedWorld={selectedSceneTemplate}
          selectedSceneTemplate={selectedSceneTemplate}
          loadScene={(name, url, type, sceneThumbnail) => {
            loadAsset({
              name,
              type,
              baseModelURL: url,
              thumbnail: url,
              sceneThumbnail,
            });
          }}
          camera={camera}
        />
      </Sider>
    </div>
  );
}
